import { coreEn } from "./core/en.js";

/** @type {import("../catalogTypes.js").MessageCatalog} */
export const en = {
  common: {
    cancel: "Cancel",
    done: "Done",
    save: "Save",
    delete: "Delete",
    rename: "Rename",
    remove: "Remove",
    share: "Share",
    unknownError: "Unknown error",
    unknownDate: "Unknown date",
    actionFailed: "Action failed",
    nameRequired: "Name required",
    nameRequiredBody: "Enter a project name.",
  },
  units: {
    kerf: "Kerf",
    margin: "Margin",
    marginValue: "Margin: {{value}}",
    standardSheet: "Stock sheet",
    thickness: "{{value}} thick",
    finalSize: "Final size {{size}}",
    edges: "Edges: {{edges}}",
    noEdges: "none",
  },
  mobile: {
    common: {
      loadingA11y: "Loading",
    },
    nav: {
      projects: "Projects",
      import: "Import",
      project: "Project",
    },
    shell: {
      eyebrow: "Workshop mode",
      brandName: "Plaatplan",
      subtitle: "Workshop cut simulator",
      splashLine1: "Import from plaatplan.nl",
      splashLine2: "Sheet layout & cut list",
      splashLine3: "Workshop mode, step by step",
    },
    projects: {
      webPromo: {
        eyebrow: "Free web planner",
        title: "Plan on plaatplan.nl",
        body:
          "Add parts at home, build sheet layouts and export your cut list, free, no account. Import the project here for step-by-step workshop mode at the saw.",
        bodyCompact:
          "Plan on the web for free, then import your project here for workshop mode.",
        showMore: "How it works",
        showLess: "Show less",
        stepPlan: "Plan parts and sheets",
        stepExport: "Export project as JSON",
        stepImport: "Import JSON in this app",
        openPlanner: "Open web planner",
        openPlannerA11y: "Open plaatplan.nl in the browser",
        toggleExpandA11y: "Show more about the web planner",
        toggleCollapseA11y: "Hide web planner details",
      },
      getStartedSection: "Get a project",
      newSection: "New project",
      lead:
        "Import a project file (.json) from the web planner, or try the sample plan.",
      newDemo: "New sample plan",
      projectLimit: "Limit: 1 project",
      importJson: "Import JSON",
      freeTierTitle: "Free tier",
      freeTierBody:
        "1 project, full cut list and sheet previews. Simulator: sheet 1 only. Import replaces your current project when the limit is reached.",
      savedProjects: "Saved projects",
      savedProjectsCount: "Saved projects ({{count}})",
      emptyTitle: "No projects yet",
      emptyBody:
        "Import a .json from plaatplan.nl, or create the sample plan to try workshop mode.",
      updatedAt: "Updated {{date}} {{time}}",
      deleteTitle: "Delete project",
      deleteBody: 'Are you sure you want to delete "{{name}}"?',
      renameTitle: "Rename project",
      importSuccessTitle: "Import complete",
      importSuccessBody: "The project is now in your project list.",
      importFailedTitle: "Import failed",
      renameA11y: "Rename {{name}}",
      deleteA11y: "Delete {{name}}",
      openA11y: "Open project {{name}}{{activeSuffix}}",
      activeSuffix: ", active project",
      listStats: "{{parts}} parts · {{sheets}} sheets",
      listStatsWithCutProgress: "{{parts}} parts · {{sheets}} sheets · {{done}}/{{total}} cut",
      emptyStepImport: "Import a .json from the web planner",
      emptyStepDemo: "Or create the sample plan with the button above",
    },
    devTools: {
      sectionTitle: "Developer",
      showOnboarding: "Show onboarding wizard",
      showOnboardingHint: "Clears the first-run flag and opens the intro flow.",
      resetFreemium: "Reset to free tier",
      resetFreemiumHint: "Locks workshop mode and keeps only one project for paywall testing.",
      resetFreemiumDoneTitle: "Free tier restored",
      resetFreemiumDoneBody: "Workshop unlock cleared. Extra projects were removed.",
      previewUnlock: "Preview unlock (dev)",
      previewUnlockHint: "Bypasses the store for local testing. Not included in production paywalls.",
    },
    detail: {
      notFound: "Project not found.",
      backToProjects: "Back to projects",
      backA11y: "Back to projects",
      shareA11y: "Share project as JSON",
      exportFailedTitle: "Export failed",
      partsAndSheets: "{{parts}} parts · {{sheets}} sheets",
      invalidParts: "{{count}} part(s) do not fit on the selected sheets.",
      partsCount: "{{count}} parts",
      sheetsCount: "{{count}} sheets",
      restCount: "{{count}} offcut",
      sheetsBlock: "Sheets",
      sheetsBlockSummary: "{{count}} sheets",
      sheetsBlockSummaryActive: "{{count}} sheets · Sheet {{number}} selected",
      sheetsToggleA11y: "Sheets, {{summary}}{{expandedSuffix}}",
      sheetsExpandedSuffix: ", expanded",
      sheetsCollapsedSuffix: ", collapsed",
      cutListBlock: "Cut list",
      resetAllParts: "Reset all",
      resetAllPartsA11y: "Reset all cut list progress",
      resetAllPartsTitle: "Reset cut progress?",
      resetAllPartsBody: "All parts will show as not cut. Workshop step progress for this project will be cleared.",
      resetAllPartsConfirm: "Reset all",
      resetPartHint: "Resets cut progress for this part",
      markPartDoneHint: "Marks this part as cut",
      markPartDoneA11y: "Mark {{name}} as cut",
      resetPartA11y: "Reset progress for {{name}}",
      resetPartTitle: "Reset this part?",
      resetPartBody: "“{{name}}” will show as not cut until you walk through those cuts again in workshop mode.",
      resetPartConfirm: "Reset",
      partRowA11y: "{{quantity}} times {{size}}, {{name}}{{doneSuffix}}",
      partDoneSuffix: ", cut complete",
      cutListOrderBy: "Order by",
      cutListOrderSubproject: "Sub-project",
      cutListOrderSheet: "Sheet",
      cutListOrderOptionA11y: "{{label}}{{selectedSuffix}}",
      cutListGroupA11y: "{{label}}, {{parts}} parts, {{pieces}} pieces{{progressSuffix}}",
      cutListGroupCounts: "{{parts}} parts, {{pieces}} pieces",
      cutListGroupMeta: "{{counts}} · {{progress}}",
      cutListGroupProgress: "{{done}}/{{total}} cut",
      cutListGroupProgressSuffix: ", {{done}} of {{total}} cut",
      cutListShow: "Show",
      cutListShowAll: "All",
      cutListShowPending: "To cut",
      cutListShowOptionA11y: "{{label}}{{selectedSuffix}}",
      cutProgressLabel: "Cut progress",
      cutProgressValue: "{{done}}/{{total}} cut",
      cutProgressA11y: "{{done}} of {{total}} parts cut",
      resumeWorkshopEyebrow: "Workshop",
      resumeWorkshop: "Continue sheet {{sheet}}, cut {{current}} of {{total}}",
      resumeWorkshopA11y: "Continue workshop on sheet {{sheet}}, cut {{current}} of {{total}}",
      resumeWorkshopAction: "Continue",
      showCut: "Show cut",
      showCutA11y: "Show cut for {{name}} in workshop mode",
      sheetLine: "Sheet {{number}}",
      sheetRest: " (offcut stock)",
      sheetParts: "{{count}} parts",
      sheetWorkshopHint: "Opens workshop mode for this sheet",
      sheetWorkshopA11y: "Open workshop for sheet {{number}}, {{parts}} parts{{restSuffix}}{{progressSuffix}}{{lockedSuffix}}{{selectedSuffix}}",
      sheetRowProgressSuffix: ", {{done}} of {{total}} cut",
      sheetRowCompleteSuffix: ", all parts cut",
      sheetRowA11y: "Sheet {{number}}, {{parts}} parts{{restSuffix}}{{lockedSuffix}}{{selectedSuffix}}",
      sheetRowLockedSuffix: ", simulator locked",
      sheetRowRestSuffix: " from rest stock",
      sheetRowSelectedSuffix: ", selected",
      plateRef: "Sheet {{plates}}",
      boardOverview: "Sheet overview",
      boardMeta: "{{count}} parts · {{thickness}} thick",
      openWorkshopA11y: "Workshop mode, sheet {{current}} of {{total}}",
      openWorkshopUnlockA11y: "Unlock simulator, sheet {{current}} of {{total}}",
      openWorkshop: "Workshop mode",
      openWorkshopUnlock: "Unlock simulator",
      sheetUnlockHint: "Unlock to walk through all {{total}} sheets step by step.",
      workshopHint: "Walk through each cut step by step at the saw, with overlays and next-step guidance.",
      defaultMaterial: "Sheet material",
      summaryA11y: "{{parts}} parts, {{newSheets}} new sheets{{restSuffix}}, utilisation {{utilisation}} percent{{cutSuffix}}",
    },
    import: {
      title: "Import project",
      failedTitle: "Import failed",
      toProjects: "Go to projects",
      importing: "Importing project…",
      pleaseWait: "Please wait…",
      projectLimit: "You can store at most one project on the free tier.",
      defaultName: "Imported project",
      notSaved: "Project was not saved.",
    },
    handoff: {
      emptyLead:
        "Open a link or QR code from plaatplan.nl, or import a project file from the project list.",
      errorLead:
        "Check whether you still have room on the free tier (1 project max), or try again from the browser.",
    },
    paywall: {
      title: "Unlock workshop mode",
      body:
        "Plan for free at plaatplan.nl. Unlock the app for multiple projects and the full step-by-step simulator at the saw.",
      eyebrow: "Workshop mode",
      sheetTitle: "Continue to the next sheet",
      sheetBody:
        "On the free tier you can complete sheet 1. Unlock to walk through every sheet step by step.",
      sheetBodyTarget:
        "Sheet {{next}} of {{total}} is ready in your plan. Unlock to follow each cut step by step at the saw.",
      sheetPreviewTitle: "Sheet {{next}} of {{total}}, preview",
      sheetPreviewBody:
        "You can view the layout. Unlock workshop mode to step through every cut on this sheet.",
      sheetCompleteTitle: "Sheet 1 complete",
      sheetCompleteBody:
        "{{remaining}} more sheets ready in the simulator. Unlock to continue step by step.",
      sheetCompleteEyebrow: "Sheet 1 done",
      webFreeNote: "Planning and printing on plaatplan.nl stays free, no subscription.",
      featureProjects: "Unlimited saved projects",
      featureSimulator: "Simulator for every sheet",
      featureWorkshop: "Full workshop mode at the saw",
      price: "€5.99",
      priceNote: "One-time purchase, no subscription",
      unlock: "Unlock",
      replaceTitle: "Replace current project?",
      replaceBody:
        "You can only store one project on the free tier. Replace your current project with this one?",
      replaceConfirm: "Replace",
      unlockedTitle: "Workshop mode unlocked",
      unlockedBody:
        "You can now save unlimited projects and run the simulator on every sheet.",
      devPreview: "Preview build: no real App Store purchase",
      devPreviewNote: "Dev builds: use DevTools for a preview unlock. Production uses App Store / Play billing.",
      sheetLimitHint: "Free tier: simulator for sheet 1 only",
      replaceUnlock: "Unlock unlimited",
      sheetSimulatorLocked: "Simulator locked",
    },
    errors: {
      invalidRecord: "Invalid project record.",
      unknownProject: "Project not found.",
      loadFailed: "Failed to load projects.",
      shareUnavailable: "Sharing is not available on this device.",
    },
    prompt: {
      namePlaceholder: "Name",
    },
    settings: {
      title: "Settings",
      menuA11y: "Open settings, current: {{summary}}",
      currentSummary: "{{language}}, {{units}}",
      language: "Language",
      units: "Units",
      languageNl: "Nederlands",
      languageEn: "English",
      unitsMetric: "Metric (mm)",
      unitsImperial: "Imperial (in)",
      workshopMode: "Workshop mode",
      workshopUnlocked: "Unlocked",
      workshopFree: "Free, sheet 1 simulator",
      upgradeWorkshop: "Unlock workshop mode",
      languageOptionA11y: "{{label}}{{selectedSuffix}}",
      unitsOptionA11y: "{{label}}{{selectedSuffix}}",
      selectedSuffix: ", selected",
      workshopStatusA11y: "Workshop mode: {{status}}",
    },
    support: {
      heading: "Support Plaatplan",
      body:
        "Plan for free at plaatplan.nl. A voluntary contribution, sharing or feedback helps the project, separate from your workshop unlock.",
      share: "Share Plaatplan",
      shareMessage: "Free cut planning for sheet goods, plan at plaatplan.nl",
      shareFailed: "Sharing failed. Please try again.",
      donate: "Buy me a coffee",
      donateUnavailable: "Donation link is not configured yet.",
      donateFailed: "Could not open the donation page.",
      feedback: "Give feedback",
      feedbackFailed: "Could not send feedback.",
      rateAppStore: "Rate in App Store",
      ratePlayStore: "Rate on Google Play",
      rateUnavailable: "Store link is not configured yet.",
      rateFailed: "Could not open the app store.",
    },
    feedback: {
      title: "Give feedback",
      subjectPrefix: "Plaatplan app feedback",
      privacyNote:
        "We do not attach your project. Only this message, the app version and platform (iOS/Android).",
    },
    iap: {
      purchasing: "Processing purchase…",
      restorePurchases: "Restore purchases",
      unavailable: "Purchases are not available in Expo Go. Use Settings → DevTools → Preview unlock (dev), or install a development build / TestFlight build for real purchases.",
      nothingToRestore: "No previous purchase found for this Apple ID or Google account.",
      purchaseFailed: "Purchase failed. Try again or restore an earlier purchase.",
      restoreSuccessTitle: "Purchase restored",
      restoreSuccessBody: "Workshop mode is unlocked again on this device.",
      restoreEmptyTitle: "Nothing to restore",
      restoreEmptyBody: "We could not find a workshop unlock for this account.",
    },
    onboarding: {
      skip: "Skip",
      skipA11y: "Skip onboarding",
      next: "Next",
      back: "Back",
      stepLabel: "{{current}} / {{total}}",
      stepA11y: "Onboarding step {{current}} of {{total}}",
      welcomeIllustrationA11y: "Illustration of a sheet layout with highlighted cut and cut list",
      preferencesIllustrationA11y: "Illustration of language and unit settings",
      workflowIllustrationA11y: "Illustration of planning on web, exporting JSON, and importing to the app",
      getStartedIllustrationA11y: "Illustration of sample plan and import options",
      welcome: {
        eyebrow: "Plaatplan Workshop",
        title: "Cut lists and guidance at the saw",
        body:
          "Plan sheet layouts on the web, then use this app in the workshop for your cut list, board previews, and step-by-step simulator.",
        featureCutList: "Cut list grouped by thickness with sheet references",
        featureBoard: "Visual sheet layout with cut sequence",
        featureWorkshop: "Workshop mode walks you through each cut",
      },
      preferences: {
        title: "Language & units",
        body: "Choose how dimensions and copy are shown. You can change this later in settings.",
      },
      workflow: {
        title: "How it works",
        body:
          "Most people plan on the free web planner and import the project here. You can also start with the built-in sample plan.",
        stepLabel: "Step {{step}}",
      },
      getStarted: {
        title: "Ready to go",
        body:
          "Try the sample plan to explore project details, or go to your project list to import a file from plaatplan.nl.",
        tryDemo: "Try sample plan",
        toProjects: "Go to projects",
        demoCardTitle: "Sample plan",
        demoCardBody: "See sheets, parts and cut layouts",
        importCardTitle: "Import later",
        importCardBody: "Bring a project from the web planner",
      },
    },
    workshop: {
      close: "Close",
      closeA11y: "Close workshop mode",
      backA11y: "Back to project",
      progressA11y: "Progress step {{current}} of {{total}}",
      previous: "Previous",
      next: "Next",
      done: "Done",
      finishSheet: "Finish sheet",
      finishSheetA11y: "Finish this sheet and return to the project",
      continueNextSheet: "Continue to next sheet",
      continueNextSheetA11y: "Continue to the next sheet",
      doneA11y: "Finish workshop and return to the project",
      back: "Back",
      rotateHint: "Rotate your device for workshop mode.",
      dismissRotateHintA11y: "Dismiss rotate hint",
      swipeHint: "Swipe left or right on the board for the previous or next cut.",
      swipeHintA11y: "Swipe on the board or use the buttons below to move between cut steps.",
      headerA11y: "{{name}}, sheet {{current}} of {{total}}",
      currentStepA11y: "Current cut step: {{title}}",
      unavailable: "Workshop mode is not available for this project.",
      noCuts: "No cuts to simulate on this sheet.",
      previousSheet: "Previous sheet",
      nextSheet: "Next sheet",
      sheetOf: "Sheet {{current}} / {{total}}",
      sheetMeta: "Sheet {{current}} of {{total}} · {{size}}",
      previousSheetA11y: "Previous sheet",
      nextSheetA11y: "Next sheet",
      sheetA11y: "Sheet {{current}} of {{total}}",
      stepProgress: "{{label}} / {{max}}",
      cutStepA11y: "Cut step",
      markAllCut: "Mark all pieces as cut",
      markAllCutA11y: "Mark all pieces on this sheet as cut in the cut list",
      markAllCutDone: "All pieces marked as cut",
      markAllCutDoneA11y: "All pieces on this sheet are marked as cut in the cut list",
    },
  },
  workshop: {
    orientation: {
      horizontal: "horizontal",
      vertical: "vertical",
    },
    orientationCap: {
      horizontal: "Horizontal",
      vertical: "Vertical",
    },
    span: {
      length: "length",
      width: "width",
    },
    caption: {
      start: "Start: full sheet on the saw.",
      stripRip: "{{orientation}} strip cut at {{position}}",
      stripCross: "Cross-cut at {{position}}",
      fullLine: "{{orientation}} cut at {{position}} across full {{span}}",
      partial: "{{orientation}} cut at {{position}}",
    },
    start: {
      detail: "Step through the numbered cuts beside the saw.",
    },
    complete: {
      title: "All cuts complete",
      detail: "Check parts for size and set aside usable offcuts.",
    },
    cut: {
      stripRip: "Rip a strip at {{position}} ({{orientation}})",
      stripCross: "Cross-cut the strip at {{position}}",
      fullLine: "Saw {{orientation}} at {{position}} across the full sheet",
      partial: "Saw {{orientation}} at {{position}}",
    },
    detail: {
      workPiece: "Work on the marked piece ({{size}}).",
      afterVertical: "After this cut: {{first}} left and {{second}} right.",
      afterHorizontal: "After this cut: {{first}} above and {{second}} below.",
    },
    stepLabel: {
      start: "Start",
      done: "Done",
      cut: "Cut {{current}} / {{max}}",
      progressCut: "Cut {{current}}",
    },
  },
  core: coreEn,
  web: {
    shell: {
      skipLink: "Skip to content",
      srTitle: "Plaatplan: free cut plan builder for sheet goods",
      eyebrow: "Free cut plan",
      closeNotice: "Close",
    },
    seo: {
      defaultTitle: "Plaatplan | Free cut plan for MDF and plywood",
      defaultDescription:
        "Create a free cut plan for MDF, plywood, particle board and furniture panels. Plaatplan nests your parts on standard sheets, counts how many sheets you need and produces a work plan with cut list for the store or cut shop.",
      ogImageAlt:
        "Plaatplan: cut layout on a sheet with parts and a result showing sheet count and utilisation",
      shareText: "Free cut planning for sheet goods: MDF, plywood and more.",
      feedbackSubjectPrefix: "Plaatplan feedback",
      features: [
        "Create cut plans for sheet goods",
        "Multiple subprojects in one plan",
        "Cut layout with kerf and grain direction",
        "Export cut list as PDF or CSV",
        "Stored locally without an account",
      ],
    },
    feedback: {
      typeBug: "Bug or error",
      typeIdea: "Idea or improvement",
      typeQuestion: "Question",
      typeOther: "Something else",
      topicLabel: "What is it about?",
      messageLabel: "Your message",
      messagePlaceholder:
        "For example: with offcut stock I cannot see why a sheet is not used.",
      emailLabel: "Email (optional)",
      emailPlaceholder: "Only if you want a reply",
      privacyNote: "We do not attach your plan. Only this message and the page you are on.",
      submit: "Send feedback",
      submitting: "Sending…",
      successTitle: "Thanks for your feedback.",
      successBody: "We read every message. If you left your email, we will try to reply.",
      sendAnother: "Send another message",
      errorUnavailable: "Sending feedback is not available right now. Try again later.",
      errorEmpty: "Write briefly what you want to report.",
      errorSendFailed: "Sending failed. Try again later.",
    },
    siteHeader: {
      brandAria: "Plaatplan, go to planner",
      openPlanner: "Open planner",
    },
    footer: {
      tagline: "Cut planning for sheet goods. Multiple projects, one work plan with cut list.",
      seoSummary: "What is a cut plan?",
      seoText:
        "A cut plan (or cut layout) shows how parts fit on MDF, plywood, particle board or furniture panels, including kerf, grain direction and offcut stock. The cut list lists cut sizes and quantities for the cutting counter. Plaatplan helps you nest panels, calculates how many sheets you need and produces a printable work plan.",
      guides: "Guides",
      handbook: "Handbook",
      info: "Information",
      supportRegion: "Support Plaatplan",
      supportTitle: "Found Plaatplan useful?",
      supportText:
        "Support the project voluntarily with a contribution, by sharing or giving feedback.",
      supportTextWithApp:
        "The web planner stays free. Support the project with a contribution, by sharing, giving feedback, or try workshop mode in the app.",
      supportButton: "Support Plaatplan",
      copyright: "© {{year}} Plaatplan",
      freeInBrowser: "Free in your browser",
      noAccount: "No account required",
      preferences: "Language & units",
      infoLinks: {
        about: "Why Plaatplan",
        faq: "FAQ",
        feedback: "Feedback",
        privacy: "Privacy",
        disclaimer: "Disclaimer",
      },
    },
    guides: {
      nav: {
        how: "How it works",
        werkplanExport: "Print work plan",
        cutSimulator: "Review cut sequence",
        importExport: "Import and export",
      },
      kicker: "Handbook",
      toPlanner: "Go to planner",
      readMore: "Read more in the guide",
      related: {
        werkplanBefore: "Cut sequence step by step? Read",
        werkplanAfter: "No plan yet? Start in the",
        werkplanOrRead: "or read",
        simulatorBefore: "More about the work plan? Read",
        simulatorAfter: "or start in the",
        planner: "planner",
      },
    },
    landing: {
      howItWorks: "How Plaatplan works",
      faq: "Frequently asked questions",
      preview: {
        title: "Example from the planner",
        intro:
          "Sheet {{sheetNumber}} from the demo plan (Chest, Desk and Cabinet on MDF {{length}} × {{width}} mm). Panels are colour-coded per subproject.",
        caption:
          "The full demo plan: {{sheets}} sheets, {{parts}} parts, {{utilisation}}% utilisation (including kerf).",
      },
      nav: {
        "zaagplan-maken": "Create cut plan",
        "zaagplan-mdf": "MDF cut plan",
        "zaagplan-multiplex": "Plywood cut plan",
        "zaagplan-spaanplaat": "Particle board cut plan",
        "zaagplan-meubelpaneel": "Furniture panel cut plan",
        "bouwmarkt-zaagplan": "Store cut plan",
        zaagindeling: "Cut layout",
      },
    },
    supportModal: {
      kicker: "Support the project",
      title: "Support Plaatplan",
      intro1:
        "Plaatplan has two parts: free cut planning in your browser, and an optional app for workshop mode next to the saw.",
      intro2:
        "The web planner stays free, no account or subscription. If Plaatplan helped you, you can support the project in several ways.",
      webHeading: "Free web planner",
      webNote:
        "Planning, nesting, work plan and export stay free in your browser. That does not change when you use the app.",
      monetaryHeading: "Voluntary contribution",
      monetaryNote:
        "A donation helps keep Plaatplan running. Payment happens outside Plaatplan, in a new tab.",
      appHeading: "Workshop mode (app)",
      appNote:
        "The app is a one-time purchase for the full simulator and multiple projects, no subscription. You can try sheet 1 free first.",
      appStoreLabel: "Download for iPhone / iPad",
      appStoreHint: "App Store, one-time unlock for workshop mode.",
      playStoreLabel: "Download for Android",
      playStoreHint: "Google Play, one-time unlock for workshop mode.",
      appComingSoon: "The app is coming to the stores soon. Export your project as JSON to import it later.",
      communityHeading: "Share or give feedback",
      shareLabel: "Share Plaatplan",
      shareHint: "Tell a DIY friend or group about the free web planner.",
      feedbackLabel: "Give feedback",
      feedbackHint: "Bug, idea or compliment, including about the app if you use it.",
      shareStatusShared: "Thanks for sharing!",
      shareStatusCopied: "Link copied to your clipboard.",
      shareStatusUnsupported: "Copying is not available here. Share manually: plaatplan.nl",
      dismiss: "Not now",
      options: {
        tikkie: { label: "Buy me a coffee", hint: "Small contribution via Tikkie." },
        mollie: { label: "Donate via iDEAL", hint: "Fixed or custom amount via Mollie." },
        github: { label: "GitHub Sponsors", hint: "One-off or monthly via GitHub." },
      },
    },
    actions: {
      import: "Import",
      export: "Export",
      loadDemo: "Load demo",
      resetPlan: "Start empty plan",
    },
    sections: {
      optimization: "Optimization",
      layout: "Cut layout",
      subprojects: "Subprojects",
      partsList: "Parts list",
      result: "Result",
      standardSheet: "Stock sheet",
      materialStock: "Material stock",
      restStock: "Offcut stock",
      project: "Project",
    },
    plan: {
      nameLabel: "Plan name",
    },
    source: {
      quickPresetsLegend: "Quick pick",
      quickPresetsHint: "Common hardware-store combinations.",
      defaultThicknessLabel: "Default thickness",
      defaultThicknessPresets: "Common default thicknesses",
      sheetSizeLegend: "Size",
      moreMaterials: "More materials",
      moreStandardFormats: "More standard sizes",
      sawSettingsLegend: "Saw settings",
      sawGrainLegend: "Saw & grain",
      kerfLabel: "Kerf width",
      marginLabel: "Edge margin",
      kerfHint: "The layout accounts for kerf width. Default is 2.2 mm.",
      grainNone: "No grain, free rotation",
      grainFixedOption: "Grain fixed on the sheet",
      grainFixedHint:
        "Panels stay in the entered orientation; no 90° rotation. Use for visible plywood.",
      grainFreeHint: "Suitable for MDF and particle board without visible grain.",
      rotationHint: "Usually on for MDF. Disabled while grain direction is fixed.",
      grainLabel: "Grain direction",
      rotationLabel: "Parts may rotate 90°",
    },
    fields: {
      length: "Length",
      width: "Width",
      thickness: "Thickness",
      quantity: "Quantity",
      unitMm: "mm",
      unitIn: "in",
      thicknessOption: "{{value}} mm",
    },
    materialStock: {
      scopeNote:
        "One material type per plan. Thicknesses may vary per part; combining two materials in the same plan is not supported.",
      scopeNoteBeforeMaterial: "One material type per plan, current choice:",
      scopeNoteAfterMaterial:
        ". Thicknesses may vary per part; combining two materials (e.g. MDF and plywood) is not supported.",
      summaryCollapsed: "Set sheet & offcut stock",
    },
    restStock: {
      introEmpty:
        "Same material as the stock sheet above. No offcut stock yet; add a piece of {{material}} you want to reuse.",
      introWithItems:
        "Same material as the stock sheet above. Choose the thickness per piece. The planner uses offcut stock before new stock sheets.",
      labelDescription: "Description",
      placeholderDescription: "E.g. left offcut",
      removeA11y: "Remove offcut stock",
      removeConfirm: "Click again to remove “{{label}}”",
      removeFallbackLabel: "offcut stock",
      addButton: "+ Add offcut stock",
    },
    planMetrics: {
      addParts: "Add parts to calculate a layout.",
      invalidSingular: "{{count}} part does not fit on the available sheets.",
      invalidPlural: "{{count}} parts do not fit on the available sheets.",
      notCalculated: "No layout calculated yet.",
      restOnlySingular: "Fits entirely on offcut stock ({{count}} piece).",
      restOnlyPlural: "Fits entirely on offcut stock ({{count}} pieces).",
      mixedSingular:
        "Fits on {{newCount}} new stock sheet and {{restCount}} offcut piece.",
      mixedPlural:
        "Fits on {{newCount}} new stock sheets and {{restCount}} offcut pieces.",
      oneSheet: "Everything fits on 1 stock sheet.",
      multipleSheets: "Fits on {{count}} stock sheets.",
    },
    confirm: {
      confirm: "Confirm",
      reset: {
        title: "Start empty plan",
        message: "Are you sure you want to start an empty plan? Your current plan will be overwritten.",
        confirmLabel: "Start empty plan",
      },
    },
    presets: {
      combined: {
        mdf18Full: { label: "MDF 18 mm", hint: "2440 × 1220" },
        mdf18Half: { label: "MDF 18 mm", hint: "1220 × 610" },
        spaan18Half: { label: "Particle board 18", hint: "1220 × 610" },
        meubelpaneel18Full: { label: "Furniture panel 18", hint: "2440 × 1220" },
      },
      material: {
        mdf18: { label: "MDF 18 mm", hint: "Most common", materialName: "MDF sheet 18 mm" },
        mdf15: { label: "MDF 15 mm", hint: "Lighter", materialName: "MDF sheet 15 mm" },
        mdf12: { label: "MDF 12 mm", materialName: "MDF sheet 12 mm" },
        mdf9: { label: "MDF 9 mm", hint: "Back panels", materialName: "MDF sheet 9 mm" },
        multiplex18: { label: "Plywood 18", hint: "Birch", materialName: "Birch plywood 18 mm" },
        multiplex15: { label: "Plywood 15", materialName: "Birch plywood 15 mm" },
        multiplex12: { label: "Plywood 12", materialName: "Birch plywood 12 mm" },
        multiplex9: { label: "Plywood 9", hint: "Back panels", materialName: "Birch plywood 9 mm" },
        spaan18: { label: "Particle board 18", hint: "Budget", materialName: "Particle board 18 mm" },
        spaan15: { label: "Particle board 15", materialName: "Particle board 15 mm" },
        spaan12: { label: "Particle board 12", materialName: "Particle board 12 mm" },
        meubelpaneel18: {
          label: "Furniture panel 18",
          hint: "Melamine / decor",
          materialName: "Furniture panel 18 mm",
        },
      },
      sheet: {
        full: { label: "2440 × 1220", hint: "Full sheet" },
        half: { label: "1220 × 610", hint: "Half sheet" },
        large: { label: "2500 × 1250", hint: "Large format" },
        panel: { label: "1830 × 750", hint: "Panel" },
      },
    },
    save: {
      autoSavedPrefix: "Saved automatically,",
      autoSavedStatus: "Saved automatically, {{time}}",
    },
    empty: {
      title: "No cut plan yet",
      copy:
        "Add parts in a subproject, or preview the sample with three furniture pieces on one plan.",
      featureKerf: "Includes kerf width",
      featurePdf: "Export as PDF afterwards",
      hint: "Chest · Desk · Cabinet, MDF 2440 × 1220 mm",
    },
    result: {
      sheetsGroupLabel: "Sheets required",
      newSheet: "New stock sheet",
      newSheets: "New stock sheets",
      restPiece: "offcut piece",
      restPieces: "offcut pieces",
      utilisationLabel: "Utilisation",
      utilisationPercent: "{{value}} percent",
      utilisationValue: "{{value}}% utilisation",
      utilisationHint: "Higher means less waste on your sheets.",
      parts: "{{count}} parts",
      partsSuffix: "parts",
      areaSuffix: "area",
      thicknessLabel: "Breakdown by thickness",
      thicknessNew: "{{count}} new",
      thicknessRest: "{{count}} offcut",
      invalid: "{{count}} part(s) do not fit.",
    },
    settings: {
      title: "Language & units",
      menuLabel: "Settings",
      menuA11y: "Open settings, current: {{summary}}",
      intro: "Choose the interface language and how dimensions are displayed.",
      currentSummary: "{{language}} · {{units}}",
      language: "Language",
      units: "Units",
      languageNl: "Nederlands",
      languageEn: "English",
      unitsMetric: "Metric (mm)",
      unitsImperial: "Imperial (in)",
    },
    messages: {
      demoLoaded: "Demo loaded: {{names}}.",
      csvDownloaded: "Cut list downloaded as CSV.",
      projectDownloaded: "Project file downloaded.",
      importSuccess: "Cut plan imported.",
      importFailed: "Import failed: {{message}}",
      resetStarted: "Empty plan started.",
    },
    board: {
      unusedTitle: "Offcut stock not used",
      sheetTitle: "Sheet {{number}}",
      restBadge: "Offcut stock",
      unusedBadge: "Not used",
      sheetMeta: "{{thickness}} thick · sheet {{index}} of this thickness",
      longAxis: "long side ({{size}})",
      shortAxis: "short side ({{size}})",
    },
    sheetTab: {
      listLabel: "Source sheets",
      unused: "Not used",
      rest: "Offcut",
      sheet: "Sheet",
    },
    layout: {
      footnote: "Colors show the subproject; parts are only combined within the same thickness.",
    },
    subproject: {
      addRegion: "Add subproject",
      addTitle: "Add subproject",
      kicker: "New subproject",
      hint: "Group parts per piece of furniture or job. Then add panels in that project.",
      defaultName: "Project {{number}}",
      placeholder: "e.g. Desk, Cabinet, Chest",
      addButton: "+ Subproject",
      nameRequired: "Give the subproject a name, for example Desk or Cabinet.",
    },
    panels: {
      unnamedPanel: "Untitled panel",
      unnamedProject: "Untitled project",
      defaultPanelName: "panel",
      panelDefaultName: "Panel",
      copySuffix: "copy",
      projectNameAria: "Subproject name",
      hiddenCount: "{{count}} hidden",
      color: "Color",
      colorTitle: "Project color",
      remove: "Remove",
      empty: "No panels in this project yet.",
      rowLabel: "row {{number}}",
      panelNumber: "Panel {{number}}",
      namePlaceholder: "e.g. left door",
      hiddenOnSheet: "hidden",
      edgeSidesSingular: "{{count}} side",
      edgeSidesPlural: "{{count}} sides",
      defineEdges: "Set edge finishing",
      showInLayout: "Show in cut layout",
      hideFromLayout: "Hide from cut layout",
      duplicate: "Duplicate panel",
      delete: "Delete panel",
      deleteConfirm: "Click again to delete “{{name}}”",
      columns: {
        number: "Number",
        name: "Panel name",
        length: "Length ({{unit}})",
        width: "Width ({{unit}})",
        thickness: "Thickness ({{unit}})",
        quantity: "Qty",
        sheet: "# Sheet",
        edges: "Edge finishing",
        edgeFinishing: "Finishing",
        actions: "Actions",
      },
      aria: {
        name: "Panel name, {{row}}",
        length: "Length in {{unit}}, {{row}}",
        width: "Width in {{unit}}, {{row}}",
        thickness: "Thickness in {{unit}}, {{row}}",
        quantity: "Quantity, {{row}}",
      },
    },
    edgeModal: {
      title: "Edge banding",
      sides: {
        top: "Top edge",
        right: "Right edge",
        bottom: "Bottom edge",
        left: "Left edge",
      },
    },
    parsePartLine: {
      empty: "Enter a part, for example 600x1220x2 side panel.",
      emptyImperial: "Enter a part, for example 24 1/2 x 12 x 2 shelf.",
      invalidFormat: "Format: length x width x quantity + name (quantity and name are optional).",
      invalidFormatImperial: "Format: length x width x quantity + name, e.g. 24 1/2 x 12 x 2 shelf or 2' 6\" x 1' 4\".",
      lengthMin: "Length must be at least {{min}}.",
      widthMin: "Width must be at least {{min}}.",
      quantityMin: "Quantity must be a whole number of 1 or higher.",
      understood: "Understood:",
      preview: "{{size}} × {{quantity}} pcs{{nameSuffix}}",
      inputAria: "Quick panel entry in {{project}}",
      placeholder: "600x1220x2 side panel",
      placeholderImperial: "24 1/2 x 12 x 2 shelf",
      addPanel: "+ Panel",
      addEmpty: "Add empty panel",
    },
    a11y: {
      unusedSummary: "Offcut stock not used",
      sheetSummary: "Text layout for sheet {{number}}",
      panelsOnSheet: "Parts on sheet {{number}}",
      rotated: ", rotated 90°",
      position: "; position {{position}} from top-left corner",
    },
    export: {
      openWerkplan: "Open work plan",
      openWerkplanHint: "Cut list, strategy and print as PDF",
      materialSummary: "What do I need",
      materialSummaryHint: "Material overview for the store",
      cutListCsv: "Cut list (CSV)",
      cutListCsvHint: "Spreadsheet for email or Excel",
      projectBackup: "Project file",
      projectBackupHint: "Download backup (.json)",
      workshopHandoff: "To workshop (app)",
      workshopHandoffHint: "QR code or link for tablet/phone",
      emptyPlanNote: "Add parts first to export a work plan.",
    },
    materialSummary: {
      kicker: "Shopping overview",
      title: "What do I need?",
      intro:
        "Take this overview to the store or cut shop. It only counts panels included in the cut layout.",
      stats: {
        newSheet: "new stock sheet",
        newSheets: "new stock sheets",
        fromRestStock: "from offcut stock",
        parts: "parts",
        utilisation: "utilisation",
      },
      invalidSingular:
        "{{count}} part does not fit on the selected stock sheet. Fix this before ordering material.",
      invalidPlural:
        "{{count}} parts do not fit on the selected stock sheet. Fix this before ordering material.",
      material: {
        title: "Material",
        plan: "Plan",
        material: "Material",
        standardSheet: "Stock sheet",
        kerf: "Kerf width",
        margin: "Edge margin",
      },
      order: {
        title: "To order",
        empty: "Add parts to calculate a material overview.",
        thickness: "Thickness",
        newSheets: "New sheets",
        restStock: "Offcut stock",
        parts: "Parts",
        sheetSize: "Size per sheet",
        partsCount: "{{count}} pcs",
        orderPrefix: "Order:",
        orderItem: "{{count}}× {{material}} ({{size}})",
        restStockLine: "From offcut stock:",
        restStockSingular: "{{count}} piece",
        restStockPlural: "{{count}} pieces",
      },
      subprojects: {
        title: "Subprojects",
        parts: "{{count}} parts",
      },
      tip: {
        title: "Tip",
        body:
          "Then open Export → Open work plan for the full cut list with sizes, edge banding and cut instructions.",
      },
    },
    mobileAppPromo: {
      eyebrow: "Plaatplan app",
      werkplan: {
        titlePhone: "Sawing yourself? Take your plan to the workshop",
        titleTablet: "Workshop mode on tablet or phone",
        bodyPhone:
          "On your phone you only see the static sheet diagram here. In the Plaatplan app you walk through every cut step by step next to the saw.",
        bodyTablet:
          "Plan for free on the web, then use the app in the shop for cut list, sheet overview and a step-by-step simulator.",
      },
      simulator: {
        title: "Step-by-step cut guide",
        body:
          "The interactive cut sequence works on a large screen. In the Plaatplan app you follow each cut fullscreen next to your saw.",
      },
      planner: {
        title: "Continue in the workshop",
        body:
          "Plan here for free. Import your project in the Plaatplan app for workshop mode: cut list, switch sheets and simulator next to the saw.",
      },
      guide: {
        how: {
          title: "Plan free, cut in the app",
          body:
            "Use Plaatplan in your browser to plan and print. When you are ready to saw, import the same project in the app for workshop mode.",
        },
        werkplanExport: {
          title: "Take it to the workshop",
          body:
            "Print the work plan for the store, or open the same project in the Plaatplan app for a step-by-step simulator beside your saw.",
        },
        cutSimulator: {
          title: "Step-by-step at the saw",
          body:
            "The web cut-sequence preview helps you prepare at home. In the app you follow every cut fullscreen in workshop mode.",
        },
        importExport: {
          title: "Same project file, offline in the shop",
          body:
            "Export a .json backup from the planner and import it in the Plaatplan app. Same format, ready for workshop mode without an account.",
        },
      },
      steps: {
        plan: "Plan on plaatplan.nl (free)",
        send: "Send to your phone or tablet",
        workshop: "Cut step by step in workshop mode",
      },
      handoff: "To app (QR or link)",
      dismiss: "Hide app promo for 24 hours",
      appStore: "App Store",
      playStore: "Google Play",
      note: "Try free: one project and sheet 1 in the simulator. No subscription.",
    },
    workshopHandoff: {
      title: "To the workshop",
      lead:
        "Scan the QR code with your tablet or phone, or tap Open in Plaatplan if the app is already on the same device.",
      close: "Close",
      qrAlt: "QR code to open the project in the Plaatplan app",
      qrLoading: "Loading QR code…",
      openInApp: "Open in Plaatplan",
      copyLink: "Copy link",
      linkCopied: "Link copied",
      copyFailed: "Copy failed. Use the QR code or Open in Plaatplan.",
      note:
        "Works on iPad and phone with the Plaatplan app installed. The project is imported automatically.",
    },
    exportBoard: {
      sheetTitle: "Sheet {{number}}",
      offcutLabel: {
        default: "OFFCUT",
        selfTrim: "TRIM YOURSELF",
      },
      stripBanner: {
        noCrosscut: "DELIVER FULL STRIPS. DO NOT TRIM TO LENGTH",
        crosscut: "DELIVER STRIPS, THEN TRIM TO CUT SIZE",
      },
      placementCaption: {
        stripSelfTrim: "CUT SIZE: trim from strip yourself",
      },
      cutSummary: {
        countSingular: "1 cut",
        countPlural: "{{count}} cuts",
        strips: "strips",
        preserve: "preserve offcuts",
        efficient: "efficient",
      },
      printSummary: {
        stripsDeliver: "Deliver as full strip: {{sizes}}.",
        stripsDefault: "Deliver as full strips; do not trim to length.",
        stripsTrimTo: "Trim to: {{sizes}}.",
        stripsSelfCut: "Expected self-trimmed: {{sizes}}.",
        preservePrimary: "If practical, include one primary offcut piece: approx. {{size}}.",
        preserveGeneric: "If practical, preserve and include larger offcut pieces.",
        efficient: "Cut efficiently according to the cut sizes in the cut list.",
      },
    },
    werkplan: {
      backToApp: "Back to app",
      printPdf: "Print / save as PDF",
      kickerPrepare: "Step 1: Prepare",
      kickerShop: "Step 2: Shop order",
      title: "Cut list and work plan",
      subtitlePrepare:
        "Choose which sheets to include, set cut settings and review the cut list. Then go to step 2.",
      subtitleShop: "Take this to the cut shop. You can adjust everything in step 1.",
      subtitlePrint:
        "Shop order. The cut dimensions in this document are authoritative. Please confirm the order is feasible.",
      facts: {
        date: "Date",
        project: "Project",
        material: "Material",
        standardSheet: "Stock sheet",
        kerf: "Kerf width",
        margin: "Edge margin",
        cutLists: "Cut lists",
        thicknessCount: "{{count}} thicknesses",
      },
      tabs: {
        listLabel: "Work plan steps",
        prepare: "Prepare",
        shop: "Shop order",
      },
      visualLayout: {
        title: "Visual cut layout",
        prepareNote:
          "Set the strategy per sheet and review the layout. The Shop order tab shows what you give to the store.",
        shopNote: "Optional reference. The cut list above is authoritative for dimensions.",
        sheetStrategyTitle: "Sheet {{number}}: cut strategy",
        strategyForSheet: "Strategy for this sheet",
        inheritStrategy: "Use general strategy",
        notPossible: " (not possible)",
        deviationBanner: "Differs from general:",
        stripRequested: "Requested:",
        stripShownAs: "Shown and exported as:",
        offcutStripSelfCut: "Part you will trim from the full strip later",
        offcutPotential: "Potential offcuts to include",
        offcutApprox: "approx. {{size}} ({{label}})",
        sheetNoteLabel: "Extra note for sheet {{number}}",
        sheetNotePlaceholder:
          "For example: cut long strips first; check for damaged edges.",
        sheetPrintPrefix: "Sheet {{number}}:",
        remark: "Note:",
      },
      shop: {
        generalInstruction: "General instruction",
        control: "Review",
      },
      alert: {
        title: "Important for cutting",
        dimensionsMetric: "All dimensions are in millimeters.",
        dimensionsImperial: "Dimensions are shown in inches.",
        cutColumns:
          "The Cut length and Cut width columns are the dimensions to cut to.",
        kerfInCalculation: "Kerf width in calculation: {{kerf}}",
        multipleThicknesses:
          "Parts with different thicknesses appear in separate cut lists and are cut separately.",
        restExcluded:
          "Offcut stock is excluded from this shop order. Only parts on new stock sheets appear in the cut list.",
        newSheetsExcluded:
          "New stock sheets are excluded from this shop order. Only parts on offcut stock appear in the cut list.",
        zeroKerfWarning:
          "Note: calculations used zero kerf width. Check whether the cut shop needs to allow for blade width.",
      },
      materialOverview: {
        title: "Material overview",
        empty: "Add parts to show a material overview.",
        material: "Material",
        sourceSize: "Source size",
        newSheets: "New sheets",
        restStock: "Offcut stock",
      },
      materialScope: {
        title: "What do you include in this shop order?",
        intro: "For {{material}}. Choose which sheets appear in the cut list and visual layouts.",
        groupLabel: "Material scope for work plan",
        newSheets: "New stock sheets",
        newSheetsDetail: "{{count}} sheet · {{size}}",
        newSheetsDetailPlural: "{{count}} sheets · {{size}}",
        newSheetsNone: "No new sheets needed · {{size}}",
        restStock: "Offcut stock",
        restDetail: "{{count}} piece from your stock",
        restDetailPlural: "{{count}} pieces from your stock",
        restNone: "No offcut stock in this plan",
        onlyNewHint:
          "Handy for the store: only new stock sheets are included. Parts on offcut stock are excluded from this shop order.",
        onlyRestHint:
          "Only offcut stock is included. Parts on new stock sheets are excluded from this shop order.",
        noneSelected: "Select at least one option to show a cut list.",
      },
      cutList: {
        title: "Cut list · {{material}}",
        prepareHint:
          "Optional: refine cut sizes and instructions per part. Use the Shop order tab for the store.",
        oversizeHint: "Cut sizes are order sizes; final sizes are in the Final size column.",
        shopHint: "Please cut to the sizes below. This is the order for the cut shop.",
        quantity: "Qty",
        orderLength: "Cut length",
        orderWidth: "Cut width",
        finalSize: "Final size",
        description: "Description",
        sheet: "Sheet",
        edges: "Edges",
        instruction: "Instruction / note",
        remark: "Note",
        resetInstruction: "Reset automatic instruction",
        resetOrderSize: "Reset cut size",
        orderLengthFor: "Cut length for {{name}}",
        orderWidthFor: "Cut width for {{name}}",
        instructionFor: "Instruction for {{name}}",
      },
      stripDelivery: {
        title: "Strips to deliver (do not trim)",
        note: "Please deliver these as full strips. I will trim them to final size myself.",
        sheet: "Sheet",
        thickness: "Thickness",
        part: "Part",
        stripSize: "Strip to deliver",
        finalSize: "Trim to",
        selfCut: "Expected self-trimmed part",
      },
      sawSettings: {
        title: "Cut settings",
        intro:
          "These settings control how cutting is done and how instructions appear in the work plan. They do not control which sheets you include.",
        strategy: {
          title: "Cut strategy",
          label: "Strategy",
          notPossible: " (not possible)",
          emphasizeExactSizes: "Emphasize cut sizes explicitly",
          showCutSequence: "Show cut lines (numbered, per strategy)",
          statusLabel: "Cut strategy status",
          overridesSingular:
            "{{overrideCount}} of {{totalCount}} sheet differs from the general strategy. See each sheet under Visual cut layout.",
          overridesPlural:
            "{{overrideCount}} of {{totalCount}} sheets differ from the general strategy. See each sheet under Visual cut layout.",
          stripsUnavailable:
            "Strip delivery does not fit this project: no sheet has a simple strip layout.",
          stripsFallback:
            "Strips are not possible here; the work plan falls back to efficient cutting.",
          stripsPartial:
            "Strips are possible on {{eligibleCount}} of {{totalCount}} sheets. Remaining sheets automatically show efficient cutting.",
          stripsActive:
            "The cut shop only saws full strips; you trim to final size at home.",
          stripDetailsSummary: "More about strip delivery",
        },
        offcuts: {
          title: "Offcuts",
          hint:
            "Leftover material on the sheet after cutting. This is not the offcut stock you already had.",
          inactiveHint:
            "Offcuts in the work plan only apply when preserving large offcuts or delivering strips.",
          minLength: "Minimum length of usable offcuts",
          minWidth: "Minimum width of usable offcuts",
          showInPlan: "Show offcuts in work plan",
        },
        orderSize: {
          title: "Order size (deliver oversize)",
          hint:
            "The cut shop saws to the cut sizes in the list. Final sizes in your plan remain authoritative for layout.",
          inactiveHint:
            "Not applicable when delivering strips. Adjust cut sizes per part manually if you want oversize delivery.",
          enable: "Deliver oversize; finish to final size at home",
          extraLength: "Extra on length (mm)",
          extraWidth: "Extra on width (mm)",
        },
        generalNote: {
          title: "General instruction",
          hint: "Free text for the cut shop, at the top of the work plan.",
          label: "General instruction",
          placeholder:
            "For example: please cut according to the cut list below; cut sizes are authoritative.",
        },
      },
      simulator: {
        enableCutSequence:
          "Turn on Show cut lines under cut strategy to review the cut sequence.",
        noCuts: "No cut lines to show step by step for this sheet and strategy.",
        teaserTitle: "Review cut sequence",
        teaserCopy:
          "Review the cut sequence at home before you print or go to the cut shop.",
        openButton: "View sequence",
        panelTitle: "Review cut sequence",
        close: "Close",
        panelHint:
          "Step through the numbered cuts to verify the sequence before printing. Earlier cuts remain visible on the final result.",
        keyboardHint: "← → to navigate.",
        previous: "Previous",
        next: "Next",
        stepSlider: "Cut step",
        stepStart: "Start",
        stepCut: "Cut {{current}}",
        stepProgress: "{{label}} / {{max}}",
        noCutsTitle: "No cuts performed yet",
        noCutsDetail: "Step through the numbered cuts to verify the sequence.",
        endLayoutTitle: "Final layout after all cuts",
        endLayoutDetail:
          "Verify the sequence is correct before you print or go to the cut shop.",
        cutStepTitle: "Cut {{number}}: {{caption}}",
        mobileStepsHeading: "Cut step overview",
        mobileStepsNote:
          "On phone and tablet you see all cuts as text here. On desktop you can step through interactively.",
      },
    },
  },
};
